import { Component, type PropType, computed, defineComponent, ref } from 'vue'
import { addUnit, buildProps, extend, truthProp } from '@xzx-design/utils'
import { Icon } from '@xzx-design/components'
import { CheckSmall } from '@xzx-design/icons-vue'
import { checkerProps } from './checker'

import type { RadioShape } from '@xzx-design/components/radio'
import type { CheckerParent, CheckerShape } from './types'
import type { UseNamespaceReturn } from '@xzx-design/hooks'

export default defineComponent({
  props: extend(
    {},
    checkerProps,
    buildProps({
      namespace: {
        type: Object as PropType<UseNamespaceReturn | undefined>,
      },
      role: String,
      shape: {
        type: String as PropType<CheckerShape | RadioShape>,
      },
      parent: {
        type: Object as PropType<CheckerParent | null>,
      },
      checked: Boolean,
      bindGroup: truthProp,
      indeterminate: {
        type: Boolean as PropType<boolean | null>,
        default: null,
      },
    })
  ),
  emits: ['click', 'toggle'],
  setup(props, { emit, slots }) {
    const iconRef = ref<HTMLElement>()

    const getParentProp = <T extends keyof CheckerParent['props']>(name: T) => {
      if (props.parent && props.bindGroup) {
        return props.parent.props[name]
      }
    }

    const disabled = computed(() => {
      if (props.parent && props.bindGroup) {
        const disabled = getParentProp('disabled') || props.disabled

        if (props.role === 'checkbox') {
          const checkedCount = (getParentProp('modelValue') as unknown[]).length
          const max = getParentProp('max')
          const overlimit = max && checkedCount >= +max

          return disabled || (overlimit && !props.checked)
        }

        return disabled
      }

      return props.disabled
    })

    const direction = computed(() => getParentProp('direction'))

    // eslint-disable-next-line vue/return-in-computed-property
    const iconStyle = computed(() => {
      const checkedColor = props.checkedColor || getParentProp('checkedColor')

      if (checkedColor && props.checked && !disabled.value) {
        return {
          borderColor: checkedColor,
          backgroundColor: checkedColor,
        }
      }
    })

    const shape = computed(() => {
      return props.shape || getParentProp('shape') || 'round'
    })

    const onClick = (event: MouseEvent) => {
      const { target } = event
      const icon = iconRef.value
      const iconClicked = icon === target || icon?.contains(target as Node)

      if (!disabled.value && (iconClicked || !props.labelDisabled)) {
        emit('toggle')
      }
      emit('click', event)
    }

    const renderIcon = () => {
      const { namespace, checked, indeterminate } = props

      const { e, em } = namespace

      const iconSize = props.iconSize || getParentProp('iconSize')

      return (
        <div
          ref={iconRef}
          class={[
            e('icon'),
            em('icon', shape.value),
            disabled.value && em('icon', 'disabled'),
            checked && em('icon', 'checked'),
            indeterminate && em('icon', 'indeterminate'),
          ]}
          style={
            shape.value !== 'dot'
              ? { fontSize: addUnit(iconSize) }
              : {
                  width: addUnit(iconSize),
                  height: addUnit(iconSize),
                  borderColor: iconStyle.value?.borderColor,
                }
          }
        >
          {slots.icon ? (
            slots.icon({ checked, disabled: disabled.value })
          ) : shape.value !== 'dot' ? (
            <Icon style={iconStyle.value}>
              {indeterminate ? <CheckSmall /> : <CheckSmall />}
            </Icon>
          ) : (
            <div
              class={e('icon--dot__icon')}
              style={{ backgroundColor: iconStyle.value?.backgroundColor }}
            ></div>
          )}
        </div>
      )
    }

    const renderLabel = () => {
      const { checked, namespace, labelPosition } = props

      const { e, em } = namespace

      if (slots.default) {
        return (
          <span
            class={[
              e('label'),
              em('label', labelPosition),
              disabled.value && em('label', 'disabled'),
            ]}
          >
            {slots.default({ checked, disabled: disabled.value })}
          </span>
        )
      }
    }

    return () => {
      const nodes: (JSX.Element | undefined)[] =
        props.labelPosition === 'left'
          ? [renderLabel(), renderIcon()]
          : [renderIcon(), renderLabel()]

      const { b, m } = props.namespace

      return (
        <div
          role={props.role}
          class={[
            b(),
            {
              [m('disabled')]: disabled.value,
              [m('label-disabled')]: props.labelDisabled,
            },
            m(direction.value),
          ]}
          tabindex={disabled.value ? undefined : 0}
          aria-checked={props.checked}
          onClick={onClick}
        >
          {nodes}
        </div>
      )
    }
  },
})
